import React, { useRef, useEffect } from "react";

/**
 * Lightweight canvas "nerve network" — floating glowing nodes connected by
 * subtle lines. High performance (no heavy 3D deps), respects reduced motion.
 */

const MAX_LINK_DIST = 130;

const createParticles = (width, height, density) => {
  const count = Math.max(24, Math.floor((width * height) / 26000));
  const n = Math.min(count, density);
  return Array.from({ length: n }, () => ({
    x: Math.random() * width,
    y: Math.random() * height,
    vx: (Math.random() - 0.5) * 0.35,
    vy: (Math.random() - 0.5) * 0.35,
    r: Math.random() * 1.8 + 0.6,
  }));
};

const moveParticle = (p, width, height) => {
  p.x += p.vx;
  p.y += p.vy;
  if (p.x < 0 || p.x > width) p.vx *= -1;
  if (p.y < 0 || p.y > height) p.vy *= -1;
};

const drawLinks = (ctx, particles, i, rgb) => {
  const p = particles[i];
  for (let j = i + 1; j < particles.length; j++) {
    const q = particles[j];
    const dx = p.x - q.x;
    const dy = p.y - q.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < MAX_LINK_DIST) {
      const alpha = (1 - dist / MAX_LINK_DIST) * 0.22;
      ctx.strokeStyle = `rgba(${rgb}, ${alpha})`;
      ctx.lineWidth = 0.6;
      ctx.beginPath();
      ctx.moveTo(p.x, p.y);
      ctx.lineTo(q.x, q.y);
      ctx.stroke();
    }
  }
};

const drawNode = (ctx, p, rgb) => {
  ctx.beginPath();
  ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
  ctx.fillStyle = `rgba(${rgb}, 0.7)`;
  ctx.shadowColor = `rgba(${rgb}, 0.85)`;
  ctx.shadowBlur = 8;
  ctx.fill();
  ctx.shadowBlur = 0;
};

const ParticleField = ({ density = 60, className = "", rgb = "31, 95, 168" }) => {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let width = 0;
    let height = 0;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let particles = [];

    const resize = () => {
      const parent = canvas.parentElement;
      width = parent.offsetWidth;
      height = parent.offsetHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = width + "px";
      canvas.style.height = height + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      particles = createParticles(width, height, density);
    };

    const draw = () => {
      ctx.clearRect(0, 0, width, height);
      for (let i = 0; i < particles.length; i++) {
        if (!reduce) moveParticle(particles[i], width, height);
        drawLinks(ctx, particles, i, rgb);
        drawNode(ctx, particles[i], rgb);
      }
      rafRef.current = requestAnimationFrame(draw);
    };

    resize();
    draw();
    window.addEventListener("resize", resize);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
    };
  }, [density, rgb]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      aria-hidden="true"
      style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
    />
  );
};

export default ParticleField;
